import type { Row } from '../../../../core/view-manager/row.js';
import type { BarState } from '../../extensions/types.js';
import { TimeAxisMath } from '../time-axis-math.js';

export interface RowRange {
  readonly top: number;
  readonly bottom: number;
}

export interface TimeRange {
  readonly leftMs: number;
  readonly rightMs: number;
}

export interface ViewportMeasurements {
  readonly scrollX: number;
  readonly scrollY: number;
  readonly width: number;
  readonly height: number;
}

export interface ViewportConfig {
  readonly rowHeight: number;
  readonly rowBuffer: number;
  readonly timeAxis: TimeAxisMath;
}

/**
 * Pure geometry for the 2D gantt viewport. Given the current scroll
 * position, the current `TimeAxisMath` and a flat row list, computes
 * which rows and which bars intersect the visible area.
 *
 * Intentionally stateless — callers re-create this cheaply on every
 * scroll tick. The pool/cache layers are separate.
 */
export class GanttViewport {
  constructor(
    private readonly measurements: ViewportMeasurements,
    private readonly config: ViewportConfig
  ) {}

  get rowRange(): RowRange {
    const { rowHeight, rowBuffer } = this.config;
    const { scrollY, height } = this.measurements;
    const top = Math.max(0, Math.floor(scrollY / rowHeight) - rowBuffer);
    const bottom = Math.ceil((scrollY + height) / rowHeight) + rowBuffer;
    return { top, bottom };
  }

  get timeRange(): TimeRange {
    const { scrollX, width } = this.measurements;
    const { timeAxis } = this.config;
    return {
      leftMs: timeAxis.xToDate(scrollX),
      rightMs: timeAxis.xToDate(scrollX + width),
    };
  }

  visibleRows(rows: readonly Row[]): Row[] {
    const { top, bottom } = this.rowRange;
    return rows.slice(top, Math.min(rows.length, bottom));
  }

  visibleBars(bars: readonly BarState[]): BarState[] {
    const { leftMs, rightMs } = this.timeRange;

    return bars.filter(bar => {
      // Time-range overlap: bar is visible if [startMs, endMs] intersects
      // [leftMs, rightMs] — the canonical range-overlap test.
      return bar.endMs >= leftMs && bar.startMs <= rightMs;
    });
  }
}
